import { useCallback, useEffect, useRef } from "react";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";
import { getOriginalFileName } from "utils/string";
import { v4 } from "uuid";

const PreviewImageModalView = ({ images, initialIndex }) => {
  const scrollRef = useRef(null);
  const containerRef = useRef(`--container${v4()}`);

  useEffect(() => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const children = Array.from(container.children);
    if (!children.length) return;

    const idx =
      typeof initialIndex === "number"
        ? Math.max(0, Math.min(children.length - 1, initialIndex))
        : 0;

    const target = children[idx];
    if (target) {
      // Ensure layout is ready before scrolling
      requestAnimationFrame(() => {
        target.scrollIntoView({
          behavior: "instant",
          block: "nearest",
        });
      });
    }
  }, [initialIndex, images]);

  const scrollToSnap = useCallback((direction) => {
    if (!scrollRef.current) return;

    const container = scrollRef.current;
    const children = Array.from(container.children);
    const scrollLeft = container.scrollLeft;

    // Determine current snap index
    let currentIndex = 0;
    for (let i = 0; i < children.length; i++) {
      const childLeft = children[i].offsetLeft;
      if (childLeft - scrollLeft >= -1) {
        currentIndex = i;
        break;
      }
    }

    let newIndex = currentIndex + direction;
    newIndex = Math.max(0, Math.min(children.length - 1, newIndex));

    const target = children[newIndex];
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, []);

  useEffect(() => {
    if (images.length <= 1) return;

    function handleKeyDown(e) {
      const tagName = e.target?.tagName?.toLowerCase();
      if (
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select" ||
        e.target?.isContentEditable
      ) {
        return;
      }

      if (e.key === "ArrowLeft") {
        e.preventDefault();
        scrollToSnap(-1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        scrollToSnap(1);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [images.length, scrollToSnap]);

  return (
    <div
      className="flex items-center images-container flex-1 p-10"
      style={{
        anchorName: containerRef.current,
        height: "calc(100vh - 160px)",
      }}
    >
      {images.length > 1 && (
        <button
          className="arrow-left cursor-pointer rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 bg-opacity-70 backdrop-blur w-16 h-16 flex items-center justify-center hover:bg-opacity-80 active:bg-opacity-100 shadow shrink-0"
          onClick={() => scrollToSnap(-1)}
          style={{
            positionAnchor: containerRef.current,
          }}
        >
          <BsChevronLeft
            className="text-gray-600 -translate-x-[2px]"
            size={32}
          />
        </button>
      )}

      <div
        ref={scrollRef}
        className="mt-0 snap-x snap-mandatory flex overflow-x-auto h-full items-center"
        style={{ scrollBehavior: "smooth", width: "100%" }}
      >
        {images.map((item, index) => (
          <div key={index} className="flex-shrink-0 w-full snap-center">
            <img
              src={item.url}
              alt={item.name}
              className="object-contain w-full aspect-video"
              style={{
                maxHeight: "calc(100vh - 200px)",
              }}
            />
            <div className="text-center mt-2">
              {getOriginalFileName(item.url)}
            </div>
          </div>
        ))}
      </div>
      {images.length > 1 && (
        <button
          className="arrow-right cursor-pointer rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 bg-opacity-70 backdrop-blur w-16 h-16 flex items-center justify-center hover:bg-opacity-80 active:bg-opacity-100 shadow shrink-0"
          onClick={() => scrollToSnap(1)}
          style={{
            positionAnchor: containerRef.current,
          }}
        >
          <BsChevronRight
            className="text-gray-600 translate-x-[2px]"
            size={32}
          />
        </button>
      )}
    </div>
  );
};

export default PreviewImageModalView;
